import React, { useCallback, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import Pagination from '@atlaskit/pagination';
import Spinner from '@atlaskit/spinner';
import { HeroBanner } from '../../components/hero-banner';
import { Search } from '../../components/search';
import Footer from '../../components/footer';
import { Header } from '../../components/header';
import type { HeaderProps } from '../../components/header';
import type { Resources } from '../../../content/types/content/resources';
import './search-results.less';
import { translatableText } from '../../utils/translatable-text';
import {
    SearchResultsDisplay,
    SearchResultCard,
    NoSearchResults
} from '../../components/search-results';
import FilterSelector from './components/filter-selector';
import ResetButton from './components/reset-button';
import {
    ContentTypeTab,
    ContentTypeTabs
} from './components/content-type-tabs';
import {
    Deployment,
    SearchOptionsApiResponse,
    SearchOptionsGenericValue,
    SortBy
} from './types';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from './state/store';
import { updateSearchTerm } from './state/search/search-reducer';
import {
    MAX_NUM_OF_RESULTS,
    NUM_OF_RESULTS_PER_PAGE,
    SearchQuery,
    fetchSearchResults
} from './state/search/utils/fetch-search-results';
import Logger from '../../../logger';
import {
    setVersionsFilterQueryValue,
    saveEntry,
    setSortByQueryValue,
    resetAllValues,
    setProductFilterValue,
    setCurrentResultIndex,
    setCategoryContentTypeFilterValues,
    setCategoryFilterValue,
    resetProductFilterValue
} from './state/filter-reducer';
import {
    ListFilter,
    MultiSelectListFilter
} from '../../components/list-filter';
import { Divider } from '../../components/divider';
import analytics from '../../utils/analytics';
import { ResultsText } from '../../components/search-results/results-text';
import { FilterTags } from '../../components/filter-tags';
import { SearchAIContainer } from '../../components/search-ai/search-ai-container';
import { getContentTypeFilterQuery, getUpdatedSearchUrl } from './utils';

export interface SearchResultsPageEntry {
    id: string;
    type: string;
    url: string;
    resources: Resources;
    header: HeaderProps;
    preview: boolean;
    searchOptions: SearchOptionsApiResponse;
}

interface SearchResultsPageProps {
    entry: SearchResultsPageEntry;
    featureFlags: {
        isAiEnabled?: boolean;
    };
}

export const SearchResultsPage = ({
    entry,
    featureFlags
}: SearchResultsPageProps) => {
    const { isAiEnabled } = featureFlags;
    const { isLoading, searchTerm, searchResponse, showPagination } =
        useSelector((state: RootState) => state.search);

    const {
        contentTypeFilterValue,
        deploymentQueryValue,
        productFilterValue,
        productsFilterValues,
        versionsQueryValue,
        versionsFilterValues,
        sortByQueryValue,
        sortByFilterValues,
        currentResultIndex,
        categoryFilterValue,
        categoryValues,
        categoryContentTypeFilterValues
    } = useSelector((state: RootState) => state.filter);

    const dispatch: AppDispatch = useDispatch();

    // used to cancel the search request if a new search request is made
    // while the previous one is still in progress
    const abortController = useRef<AbortController | null>(null);

    const searchHandler = async (
        dispatch: AppDispatch,
        searchTerm: string,
        query: SearchQuery,
        searchStartTime: number
        // eslint-disable-next-line max-params
    ) => {
        if (abortController.current) {
            abortController.current.abort();
        }

        try {
            abortController.current = new AbortController();

            await dispatch(
                fetchSearchResults({
                    searchTerm,
                    query,
                    signal: abortController.current.signal
                })
            ).unwrap();

            const searchResponseTime = Date.now() - searchStartTime;
            analytics.sendGASV3OperationalEvent(
                {
                    action: 'rendered',
                    actionSubject: 'results',
                    actionSubjectId: 'searchResults'
                },
                { searchTerm, responseTime: searchResponseTime }
            );
        } catch (error) {
            // For AbortError, we don't need to do anything since the isLoading state
            // will be kept true in the reducer until the new request completes
            if ((error as Error).name !== 'AbortError') {
                Logger.error({ error }, 'Error dispatching the search handler');
            }
        }
    };

    const debouncedQueryHelpAggregator = debounce(searchHandler, 500);

    const handleSearchChange = (searchValue: string) => {
        // @ts-ignore
        const action = updateSearchTerm(searchValue);
        dispatch(action);
    };

    const handleSubmit = (searchValue: string) => {
        if (!searchValue) {
            return;
        }

        const searchStartTime = Date.now();
        const deployment = deploymentQueryValue;
        const product = productFilterValue.key;
        const version = versionsQueryValue[0] || '';

        const contentTypeFilterQuery = getContentTypeFilterQuery({
            categoryContentTypeFilterValues,
            categoryFilterValue,
            categoryValues
        });

        const contentTypeFilter =
            categoryContentTypeFilterValues?.map(
                (contentType) => contentType.key
            ) ?? [];

        const updatedUrl = getUpdatedSearchUrl({
            searchValue,
            deployment,
            product,
            version,
            contentTypeFilter
        });

        window.history.replaceState({}, '', updatedUrl);

        const productQueryValue = product ? [product] : [];

        debouncedQueryHelpAggregator(
            dispatch,
            searchTerm,
            {
                deployment,
                productsQueryValue: productQueryValue,
                contentTypes: contentTypeFilterQuery,
                version: versionsQueryValue,
                sortByQueryValue,
                pageIndex: currentResultIndex
            },
            searchStartTime
        );
    };

    const handleSortByFilterChange = (value: SortBy) => {
        const oldSortByQueryValue = sortByQueryValue;
        dispatch(setSortByQueryValue(value));
        analytics.trackEvent('Sort by filter', {
            event: 'clicked',
            eventComponent: 'filter',
            actionSubjectId: 'sort-by-filter',
            category: 'search results filter',
            action: 'clicked',
            currentSelection: oldSortByQueryValue,
            newSelection: value
        });
    };

    useEffect(() => {
        handleSubmit(searchTerm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        deploymentQueryValue,
        productFilterValue,
        contentTypeFilterValue,
        versionsQueryValue,
        sortByQueryValue,
        currentResultIndex,
        categoryContentTypeFilterValues,
        categoryFilterValue
    ]);

    // Initialize the search query from the url on first visit
    useEffect(() => {
        const parsedUrl = new URL(window.location.href);
        const searchTermFromUrl = parsedUrl.searchParams.get('searchTerm');
        const deploymentFromUrl = parsedUrl.searchParams.get('deployment');
        const productFromUrl = parsedUrl.searchParams.get('product');
        const versionFromUrl = parsedUrl.searchParams.get('version');
        const contentTypeFromUrl = parsedUrl.searchParams.get('contentType');

        const product = entry?.searchOptions?.filters?.platforms
            .filter((platform) => platform.key === deploymentFromUrl)?.[0]
            ?.products.filter((product) => product.key === productFromUrl)?.[0];

        const contentTypeFiltersFromURL: SearchOptionsGenericValue[] =
            contentTypeFromUrl
                ? contentTypeFromUrl
                      .split(',')
                      .map((contentType) => {
                          const foundContentType =
                              entry?.searchOptions?.filters?.categories
                                  .find(({ key }) => key === 'all')
                                  ?.contentTypes.find(
                                      ({ key }) => key === contentType
                                  );
                          return foundContentType as SearchOptionsGenericValue;
                      })
                      .filter(Boolean)
                : [];

        // Batch all the initialization actions
        dispatch(setCategoryContentTypeFilterValues(contentTypeFiltersFromURL));
        dispatch(updateSearchTerm(searchTermFromUrl ?? ''));
        dispatch(
            setProductFilterValue({
                deployment: (deploymentFromUrl as Deployment) || undefined,
                product: {
                    ...product,
                    label: ''
                }
            })
        );
        dispatch(
            setVersionsFilterQueryValue(versionFromUrl ? [versionFromUrl] : [])
        );
        dispatch(saveEntry(entry));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const renderNewVersionFilter = useCallback(() => {
        if (
            deploymentQueryValue === Deployment.DATA_CENTER &&
            versionsFilterValues?.length > 0
        ) {
            return (
                <>
                    <Divider />
                    <ListFilter
                        title={'Version'}
                        selected={{
                            key: versionsQueryValue[0] || ''
                        }}
                        optionsLists={[{ options: versionsFilterValues }]}
                        isScrollable={true}
                        onChange={(option) => {
                            dispatch(setVersionsFilterQueryValue([option.key]));
                        }}
                        analyticsMetadata={{
                            selectedProduct: productFilterValue.key
                        }}
                    />
                </>
            );
        }
    }, [
        deploymentQueryValue,
        versionsFilterValues,
        versionsQueryValue,
        productFilterValue.key,
        dispatch
    ]);

    const renderSearchResults = useCallback(
        () =>
            searchResponse.results.map((result: any, index: number) => (
                <SearchResultCard
                    key={result.content.contentAri}
                    id={result.content.contentAri}
                    title={result.content.title}
                    description={
                        result?.highlights?.description ||
                        result.content.description
                    }
                    href={result.content.url}
                    dateModified={result.content.metadata.lastPublishedAt}
                    markedUseful={
                        result.content.metadata?.trustFactors?.helpfulCount
                    }
                    views={result.content.metadata?.trustFactors?.numViews}
                    searchTerm={searchTerm}
                    position={index + 1}
                    queryId={searchResponse.queryId}
                    products={result.content.products}
                    productIcons={entry.header.productIcons}
                />
            )),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchResponse, entry.header.productIcons]
    );

    const renderProductFilter = useCallback(() => {
        return (
            <ListFilter
                onChange={(option, headerKey) => {
                    dispatch(
                        setProductFilterValue({
                            deployment: headerKey as Deployment,
                            product: option
                        })
                    );
                }}
                title="App"
                selected={{
                    key: productFilterValue.key,
                    headerKey: deploymentQueryValue
                }}
                optionsLists={productsFilterValues.map((platform) => ({
                    header: {
                        key: platform.key,
                        name: platform.name
                    },
                    options: platform.products.map((product) => {
                        const label = `${product.name}`;

                        return {
                            ...product,
                            label: label
                        };
                    })
                }))}
            />
        );
    }, [
        deploymentQueryValue,
        dispatch,
        productFilterValue.key,
        productsFilterValues
    ]);

    const originalQueryFromMetaData =
        searchResponse?.metadata?.query?.originalQuery;
    const paginationSize = searchResponse?.pagination?.size;
    const totalPageCount = searchResponse?.pagination?.total;

    const renderTotalResults = useCallback(() => {
        const startCount = currentResultIndex + 1;
        const searchTermToDisplay = isLoading
            ? searchTerm
            : originalQueryFromMetaData ?? searchTerm;

        return (
            <ResultsText
                searchTerm={searchTermToDisplay}
                startCount={startCount}
                endCount={startCount + paginationSize - 1}
                totalCount={totalPageCount}
            />
        );
    }, [
        currentResultIndex,
        isLoading,
        searchTerm,
        originalQueryFromMetaData,
        paginationSize,
        totalPageCount
    ]);

    const renderFilterTags = () => {
        const tags = [];
        if (productFilterValue.key) {
            tags.push({
                type: 'product',
                key: productFilterValue.key,
                name: productFilterValue.name,
                analyticsMetadata: {
                    searchTerm,
                    clearedFilterTags: [
                        productFilterValue.key,
                        ...versionsQueryValue
                    ]
                },
                onRemove: () => dispatch(resetProductFilterValue())
            });

            versionsQueryValue.forEach((version) =>
                tags.push({
                    type: 'version',
                    key: version,
                    name: version,
                    analyticsMetadata: {
                        searchTerm,
                        clearedFilterTags: [version]
                    },
                    onRemove: () =>
                        dispatch(
                            setVersionsFilterQueryValue(
                                versionsQueryValue.filter((v) => v !== version)
                            )
                        )
                })
            );
        }

        if (categoryContentTypeFilterValues.length > 0) {
            categoryContentTypeFilterValues.forEach((contentType) =>
                tags.push({
                    type: 'contentType',
                    key: contentType.key,
                    name: contentType.name,
                    analyticsMetadata: {
                        searchTerm,
                        clearedFilterTags: [contentType.key]
                    },
                    onRemove: () =>
                        dispatch(
                            setCategoryContentTypeFilterValues(
                                categoryContentTypeFilterValues.filter(
                                    ({ key }) => key !== contentType.key
                                )
                            )
                        )
                })
            );
        }

        return <FilterTags tags={tags} />;
    };

    const renderClearFilterButton = () => {
        if (
            productFilterValue.key ||
            categoryContentTypeFilterValues.length > 0
        ) {
            return (
                <React.Fragment>
                    <ResetButton
                        appearance="default"
                        analyticsMetadata={{
                            searchTerm,
                            clearedDeployment: deploymentQueryValue,
                            clearedProducts: [productFilterValue.key],
                            clearedVersions: versionsQueryValue,
                            clearedContentTypes:
                                categoryContentTypeFilterValues.map(
                                    ({ key }) => key
                                )
                        }}
                        onReset={() => {
                            const shouldReset =
                                !!productFilterValue.key ||
                                categoryContentTypeFilterValues.length > 0;
                            if (shouldReset) {
                                dispatch(resetAllValues());
                            }
                            return shouldReset;
                        }}
                    />
                    <Divider />
                </React.Fragment>
            );
        }

        return null;
    };

    const renderPagination = () => {
        return (
            <div className="pagination" data-testid="search-results-pagination">
                <Pagination
                    testId="pagination"
                    pages={
                        searchResponse.pagination
                            ? Array.from(
                                  {
                                      length: Math.min(
                                          Math.ceil(
                                              searchResponse.pagination?.total /
                                                  NUM_OF_RESULTS_PER_PAGE
                                          ),
                                          MAX_NUM_OF_RESULTS /
                                              NUM_OF_RESULTS_PER_PAGE
                                      )
                                  },
                                  (_, i) => i + 1
                              )
                            : []
                    }
                    selectedIndex={Math.floor(
                        currentResultIndex / NUM_OF_RESULTS_PER_PAGE
                    )}
                    onChange={(
                        _: React.SyntheticEvent<Element, Event>,
                        page: number
                    ) => {
                        const oldPage =
                            Math.floor(
                                currentResultIndex / NUM_OF_RESULTS_PER_PAGE
                            ) + 1;

                        if (oldPage !== page) {
                            const newResultIndex =
                                (page - 1) * NUM_OF_RESULTS_PER_PAGE;
                            dispatch(setCurrentResultIndex(newResultIndex));
                            analytics.trackEvent('pagination', {
                                event: 'clicked',
                                eventComponent: 'pagination',
                                actionSubjectId: 'pagination',
                                category: 'pagination',
                                action: 'clicked',
                                currentSelection: oldPage,
                                newSelection: page
                            });
                        }
                    }}
                />
            </div>
        );
    };

    const renderCategoryTabs = useCallback(() => {
        const tabs: ContentTypeTab[] = [];

        categoryValues.forEach((categoryType) => {
            tabs.push({
                key: categoryType.key,
                name: categoryType.name,
                label: `${categoryType.name}`,
                testId: `${categoryType.key}-tab`
            });
        });

        const handleTabSelect = (selectedCategoryType: ContentTypeTab) => {
            const previousValue = categoryFilterValue.key;
            dispatch(setCategoryFilterValue(selectedCategoryType));
            return previousValue;
        };

        return (
            <ContentTypeTabs
                tabs={tabs}
                onSelect={handleTabSelect}
                testId="category-type-tabs"
            />
        );
    }, [categoryValues, categoryFilterValue.key, dispatch]);

    const renderContentTypeFilter = useCallback(() => {
        const selectedCategory = categoryValues.find(
            (category) => category.key === categoryFilterValue.key
        );

        const contentTypeOptions: SearchOptionsGenericValue[] =
            selectedCategory?.contentTypes?.map((contentType) => ({
                key: contentType.key,
                name: contentType.name
            })) || [];

        return (
            <MultiSelectListFilter
                title="Content Type"
                selected={categoryContentTypeFilterValues}
                optionsLists={[
                    {
                        options: contentTypeOptions
                    }
                ]}
                onChange={(selected) => {
                    dispatch(setCategoryContentTypeFilterValues(selected));
                }}
            />
        );
    }, [
        categoryValues,
        categoryContentTypeFilterValues,
        categoryFilterValue.key,
        dispatch
    ]);

    const subHeaderText = translatableText.heroBannerSubheader.text;

    const placeholder = translatableText.searchPlaceholderV2.text;

    const renderSearchResultArea = useCallback(() => {
        if (isLoading && !!searchTerm) {
            return (
                <div className="search-results-spinner-container">
                    <Spinner size={'xlarge'} />
                </div>
            );
        } else if (searchResponse?.queryId && searchResponse.results?.length) {
            return renderSearchResults();
        } else {
            return <NoSearchResults />;
        }
    }, [isLoading, searchResponse, searchTerm, renderSearchResults]);

    return (
        <div className="search-results" data-testid="page-template">
            <div className="content-wrapper">
                <Header
                    id={entry.header.id}
                    pageId={entry.id}
                    resources={entry.resources}
                    additionalLinks={entry.header.additionalLinks}
                    atlassianLogo={entry.header.atlassianLogo}
                    atlassianSupportLogoV2={entry.header.atlassianSupportLogoV2}
                    atlassianSupportLogoV3={entry.header.atlassianSupportLogoV3}
                    logo={entry.header.atlassianSupportLogo.url}
                    showPreviewBanner={entry.preview}
                    atlassianSupportLogo={entry.header.atlassianSupportLogo}
                    serverProducts={entry.header.serverProducts}
                    cloudProducts={entry.header.cloudProducts}
                    menus={entry.header.menus}
                    isNavRedesignEnabled={true}
                    renderV2={true}
                />
                <main id="maincontent">
                    <HeroBanner
                        searchField={
                            <Search
                                onSearch={handleSubmit}
                                placeholder={placeholder}
                                initialValue={searchTerm}
                                analyticsMetadata={{
                                    parentComponent: 'search-results'
                                }}
                                onChange={handleSearchChange}
                            />
                        }
                        subHeader={subHeaderText}
                        {...(isAiEnabled ? { appearance: 'minimal' } : {})}
                    />
                    <div
                        data-testid="search-results-page"
                        className="search-results-page"
                    >
                        <div className="above-search-cards-container">
                            <div className="content-type-tabs">
                                {renderCategoryTabs()}
                            </div>
                            <div className="search-results-summary">
                                {!isLoading && renderTotalResults()}
                            </div>
                            <div className="sort-by-filter-section">
                                <div className="sort-by-filter-text">
                                    {translatableText.sortBy.text}
                                </div>
                                <FilterSelector
                                    filterTitle={translatableText.sortBy.text}
                                    filterValues={sortByFilterValues}
                                    allowFilterAll={false}
                                    setQueryValues={handleSortByFilterChange}
                                    queryByString
                                    defaultSelected={sortByFilterValues[0].name}
                                    showBorder
                                />
                            </div>
                        </div>
                        <div className="search-results-grid">
                            <div className="search-filters">
                                {renderClearFilterButton()}
                                {renderFilterTags()}
                                <Divider />
                                {renderContentTypeFilter()}
                                {renderProductFilter()}
                                {renderNewVersionFilter()}
                            </div>
                            <SearchResultsDisplay>
                                {searchResponse?.queryId &&
                                    searchResponse.results?.length !== 0 && (
                                        <SearchAIContainer
                                            searchTerm={
                                                searchResponse?.metadata?.query
                                                    ?.originalQuery
                                            }
                                        />
                                    )}
                                {renderSearchResultArea()}
                            </SearchResultsDisplay>
                        </div>
                        {showPagination && renderPagination()}
                    </div>
                </main>
            </div>
            <Footer
                logo={entry.header.atlassianLogo.url}
                className={'margin-top-large'}
            />
        </div>
    );
};
